import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp';
import { motion } from 'framer-motion';

const Wallet = () => {
  const navigate = useNavigate();
  const { language } = useApp();
  const [activeTab, setActiveTab] = useState('transactions');
  const [showAddMoneyModal, setShowAddMoneyModal] = useState(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [amount, setAmount] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

  // Sample wallet data
  const [walletData, setWalletData] = useState({
    balance: 5000,
    currency: '₹',
    transactions: [
      {
        id: 'txn001',
        type: 'credit',
        amount: 2000,
        description: 'Added money to wallet',
        date: '2023-12-15T10:30:00',
        status: 'completed'
      },
      {
        id: 'txn002',
        type: 'debit',
        amount: 1500,
        description: 'Consultation fee - Adv. Priya Sharma',
        date: '2023-12-10T14:45:00',
        status: 'completed'
      },
      {
        id: 'txn003',
        type: 'credit',
        amount: 4500,
        description: 'Refund for cancelled consultation',
        date: '2023-11-28T09:15:00',
        status: 'completed'
      }
    ]
  });

  const translations = {
    english: {
      title: 'Wallet',
      subtitle: 'Manage your funds',
      balance: 'Current Balance',
      addMoney: 'Add Money',
      withdraw: 'Withdraw',
      transactions: 'Transactions',
      cards: 'Cards',
      history: 'History',
      noTransactions: 'No transactions yet',
      viewAll: 'View All',
      date: 'Date',
      description: 'Description',
      amount: 'Amount',
      status: 'Status',
      completed: 'Completed',
      pending: 'Pending',
      failed: 'Failed',
      enterAmount: 'Enter Amount',
      continue: 'Continue',
      cancel: 'Cancel',
      selectPaymentMethod: 'Select Payment Method',
      creditCard: 'Credit Card',
      debitCard: 'Debit Card',
      upi: 'UPI',
      netBanking: 'Net Banking',
      payNow: 'Pay Now',
      back: 'Back'
    },
    hindi: {
      title: 'वॉलेट',
      subtitle: 'अपने फंड प्रबंधित करें',
      balance: 'वर्तमान शेष राशि',
      addMoney: 'पैसे जोड़ें',
      withdraw: 'निकासी',
      transactions: 'लेन-देन',
      cards: 'कार्ड',
      history: 'इतिहास',
      noTransactions: 'अभी तक कोई लेनदेन नहीं',
      viewAll: 'सभी देखें',
      date: 'तारीख',
      description: 'विवरण',
      amount: 'राशि',
      status: 'स्थिति',
      completed: 'पूर्ण',
      pending: 'लंबित',
      failed: 'विफल',
      enterAmount: 'राशि दर्ज करें',
      continue: 'जारी रखें',
      cancel: 'रद्द करें',
      selectPaymentMethod: 'भुगतान विधि चुनें',
      creditCard: 'क्रेडिट कार्ड',
      debitCard: 'डेबिट कार्ड',
      upi: 'यूपीआई',
      netBanking: 'नेट बैंकिंग',
      payNow: 'अभी भुगतान करें',
      back: 'वापस'
    }
  };

  const t = translations[language] || translations.english;

  const tabs = [
    { id: 'transactions', label: t.transactions },
    { id: 'cards', label: t.cards },
    { id: 'history', label: t.history }
  ];

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-700 border border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 border border-yellow-200';
      case 'failed':
        return 'bg-red-100 text-red-700 border border-red-200';
      default:
        return 'bg-blue-100 text-blue-700 border border-blue-200';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const handleAddMoney = () => {
    setShowAddMoneyModal(true);
  };

  const handleContinue = () => {
    if (amount && !isNaN(amount) && parseFloat(amount) > 0) {
      setShowAddMoneyModal(false);
      setShowPaymentMethodModal(true);
    }
  };

  const handlePayNow = () => {
    if (selectedPaymentMethod && amount) {
      // Simulate adding money
      const amountValue = parseFloat(amount);
      const newTransaction = {
        id: `txn${Math.floor(Math.random() * 10000)}`,
        type: 'credit',
        amount: amountValue,
        description: `Added money via ${selectedPaymentMethod}`,
        date: new Date().toISOString(),
        status: 'completed'
      };

      setWalletData(prev => ({
        ...prev,
        balance: prev.balance + amountValue,
        transactions: [newTransaction, ...prev.transactions]
      }));

      // Reset states
      setShowPaymentMethodModal(false);
      setAmount('');
      setSelectedPaymentMethod(null);
    }
  };

  const handleCancel = () => {
    setShowAddMoneyModal(false);
    setShowPaymentMethodModal(false);
    setAmount('');
    setSelectedPaymentMethod(null);
  };

  const handleBack = () => {
    setShowPaymentMethodModal(false);
    setShowAddMoneyModal(true);
  };

  const handleWithdraw = () => {
    // Simulate withdrawal
    const amount = 500; // In a real app, this would come from user input
    const newTransaction = {
      id: `txn${Math.floor(Math.random() * 10000)}`,
      type: 'debit',
      amount: amount,
      description: 'Withdrawal to bank account',
      date: new Date().toISOString(),
      status: 'pending'
    };

    setWalletData(prev => ({
      ...prev,
      balance: prev.balance - amount,
      transactions: [newTransaction, ...prev.transactions]
    }));
  };

  return (
    <div className="screen-content bg-bg-primary">
      {/* iPhone 14 Pro Header */}
      <div className="app-header">
        <div className="header-content">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="back-button"
              aria-label="Go back"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="header-title">{t.title}</h1>
            <p className="header-subtitle">{t.subtitle}</p>
          </div>
        </div>
      </div>

      {/* iPhone 14 Pro Main Content */}
      <div className="container mobile-padding space-y-6">
        {/* Balance Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-accent-primary to-accent-secondary rounded-2xl p-6 shadow-md w-full text-white"
        >
          <div className="text-center">
            <p className="text-sm font-medium opacity-90 mb-2">{t.balance}</p>
            <h2 className="text-3xl font-bold mb-4">{walletData.currency} {walletData.balance.toLocaleString()}</h2>
            <div className="flex justify-center gap-4">
              <button 
                onClick={handleAddMoney}
                className="bg-white/20 hover:bg-white/30 text-white py-2 px-4 rounded-xl transition-colors duration-200"
              >
                {t.addMoney}
              </button>
              <button 
                onClick={handleWithdraw}
                className="bg-white/20 hover:bg-white/30 text-white py-2 px-4 rounded-xl transition-colors duration-200"
              >
                {t.withdraw}
              </button>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex bg-bg-elevated rounded-2xl p-1 w-full"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-all duration-200 touch-friendly ${
                activeTab === tab.id
                  ? 'bg-bg-card text-text-primary shadow-md'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </motion.div>

        {/* Transactions List */}
        {activeTab === 'transactions' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-4"
          >
            {walletData.transactions.length > 0 ? (
              walletData.transactions.map((transaction, index) => (
                <motion.div
                  key={transaction.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="bg-bg-card border border-border rounded-2xl shadow-md overflow-hidden w-full"
                >
                  <div className="p-4 sm:p-6">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-text-secondary text-sm">{formatDate(transaction.date)}</p>
                        <p className="text-text-tertiary text-xs">{formatTime(transaction.date)}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(transaction.status)}`}>
                        {t[transaction.status]}
                      </span>
                    </div>
                    <p className="text-text-primary font-medium mb-2">{transaction.description}</p>
                    <div className="flex justify-between items-center">
                      <p className={`font-bold ${transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                        {transaction.type === 'credit' ? '+' : '-'} {walletData.currency} {transaction.amount.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-12"
              >
                <div className="w-20 h-20 bg-bg-elevated rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">💸</span>
                </div>
                <h3 className="text-lg font-semibold text-text-primary mb-2">{t.noTransactions}</h3>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Cards Tab Content */}
        {activeTab === 'cards' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-center py-12"
          >
            <div className="w-20 h-20 bg-bg-elevated rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">💳</span>
            </div>
            <h3 className="text-lg font-semibold text-text-primary mb-2">No cards added yet</h3>
            <p className="text-text-secondary text-sm mb-6">Add a card to make payments easier</p>
            <button className="btn-primary py-3 px-6 rounded-xl">
              Add New Card
            </button>
          </motion.div>
        )}

        {/* History Tab Content */}
        {activeTab === 'history' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-md w-full"
          >
            <div className="space-y-4">
              <div className="flex justify-between items-center pb-2 border-b border-border">
                <p className="text-text-secondary text-sm font-medium">{t.date}</p>
                <p className="text-text-secondary text-sm font-medium">{t.amount}</p>
              </div>
              
              {walletData.transactions.map((transaction) => (
                <div key={transaction.id} className="flex justify-between items-center py-2 border-b border-border/50 last:border-0">
                  <div>
                    <p className="text-text-primary text-sm font-medium">{formatDate(transaction.date)}</p>
                    <p className="text-text-tertiary text-xs">{transaction.description}</p>
                  </div>
                  <p className={`text-sm font-bold ${transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                    {transaction.type === 'credit' ? '+' : '-'} {walletData.currency} {transaction.amount.toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Add Money Modal */}
      {showAddMoneyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-bg-card rounded-2xl p-6 w-[90%] max-w-md shadow-xl"
          >
            <h3 className="text-xl font-bold text-text-primary mb-6 text-center">{t.enterAmount}</h3>
            
            <div className="mb-6">
              <div className="flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-text-primary mr-2">{walletData.currency}</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0"
                  className="text-3xl font-bold text-text-primary bg-transparent border-b-2 border-accent-primary w-40 text-center focus:outline-none"
                />
              </div>
            </div>
            
            <div className="flex gap-4">
              <button 
                onClick={handleCancel}
                className="flex-1 py-3 px-4 rounded-xl text-text-primary border border-border hover:bg-bg-elevated transition-colors"
              >
                {t.cancel}
              </button>
              <button 
                onClick={handleContinue}
                className="flex-1 py-3 px-4 rounded-xl bg-accent-primary text-white hover:bg-accent-primary/90 transition-colors"
                disabled={!amount || isNaN(amount) || parseFloat(amount) <= 0}
              >
                {t.continue}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Payment Method Modal */}
      {showPaymentMethodModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-bg-card rounded-2xl p-6 w-[90%] max-w-md shadow-xl"
          >
            <div className="flex items-center mb-6">
              <button 
                onClick={handleBack}
                className="p-2 rounded-full hover:bg-bg-elevated transition-colors"
                aria-label="Go back"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h3 className="text-xl font-bold text-text-primary ml-2">{t.selectPaymentMethod}</h3>
            </div>
            
            <div className="space-y-4 mb-6">
              <div 
                onClick={() => setSelectedPaymentMethod(t.creditCard)}
                className={`p-4 rounded-xl border ${selectedPaymentMethod === t.creditCard ? 'border-accent-primary bg-accent-primary/10' : 'border-border'} cursor-pointer hover:bg-bg-elevated transition-colors`}
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-accent-primary/20 rounded-full flex items-center justify-center mr-4">
                    <span className="text-lg">💳</span>
                  </div>
                  <span className="font-medium text-text-primary">{t.creditCard}</span>
                </div>
              </div>
              
              <div 
                onClick={() => setSelectedPaymentMethod(t.debitCard)}
                className={`p-4 rounded-xl border ${selectedPaymentMethod === t.debitCard ? 'border-accent-primary bg-accent-primary/10' : 'border-border'} cursor-pointer hover:bg-bg-elevated transition-colors`}
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-accent-primary/20 rounded-full flex items-center justify-center mr-4">
                    <span className="text-lg">💳</span>
                  </div>
                  <span className="font-medium text-text-primary">{t.debitCard}</span>
                </div>
              </div>
              
              <div 
                onClick={() => setSelectedPaymentMethod(t.upi)}
                className={`p-4 rounded-xl border ${selectedPaymentMethod === t.upi ? 'border-accent-primary bg-accent-primary/10' : 'border-border'} cursor-pointer hover:bg-bg-elevated transition-colors`}
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-accent-primary/20 rounded-full flex items-center justify-center mr-4">
                    <span className="text-lg">📱</span>
                  </div>
                  <span className="font-medium text-text-primary">{t.upi}</span>
                </div>
              </div>
              
              <div 
                onClick={() => setSelectedPaymentMethod(t.netBanking)}
                className={`p-4 rounded-xl border ${selectedPaymentMethod === t.netBanking ? 'border-accent-primary bg-accent-primary/10' : 'border-border'} cursor-pointer hover:bg-bg-elevated transition-colors`}
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-accent-primary/20 rounded-full flex items-center justify-center mr-4">
                    <span className="text-lg">🏦</span>
                  </div>
                  <span className="font-medium text-text-primary">{t.netBanking}</span>
                </div>
              </div>
            </div>
            
            <div className="flex gap-4">
              <button 
                onClick={handleCancel}
                className="flex-1 py-3 px-4 rounded-xl text-text-primary border border-border hover:bg-bg-elevated transition-colors"
              >
                {t.cancel}
              </button>
              <button 
                onClick={handlePayNow}
                className="flex-1 py-3 px-4 rounded-xl bg-accent-primary text-white hover:bg-accent-primary/90 transition-colors"
                disabled={!selectedPaymentMethod}
              >
                {t.payNow}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Wallet;